import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Play, Pause, ArrowLeft, ExternalLink, Send, Loader2, Radio as RadioIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Footer } from "@/components/site/Footer";
import { useRadio, mediaUrl } from "@/context/RadioContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GENRES = ["ALL", "Exclusive", "Beat", "Single", "Remix"];

// Turn a streaming link into an on-page player embed (Spotify, YouTube, SoundCloud, Apple Music).
function embedFor(url) {
  if (!url) return null;
  let m;
  if ((m = url.match(/open\.spotify\.com\/(album|track|playlist|episode|artist)\/([A-Za-z0-9]+)/)))
    return { src: `https://open.spotify.com/embed/${m[1]}/${m[2]}?theme=0`, height: 152, platform: "Spotify" };
  if ((m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/)))
    return { src: `https://www.youtube.com/embed/${m[1]}?autoplay=1`, height: 220, platform: "YouTube" };
  if (/music\.apple\.com/.test(url))
    return { src: url.replace("music.apple.com", "embed.music.apple.com"), height: 175, platform: "Apple Music" };
  if (/soundcloud\.com/.test(url))
    return { src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23e30613&auto_play=true&hide_related=true&show_comments=false&visual=false`, height: 166, platform: "SoundCloud" };
  return null;
}

const TopBar = () => (
  <header className="fixed top-0 left-0 right-0 z-[1000] bg-black/70 backdrop-blur-xl border-b border-white/10">
    <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
      <Link to="/" data-testid="radio-logo" className="font-display text-2xl md:text-3xl tracking-tight leading-none">
        <span className="text-[#E30613]">VL</span><span className="text-white">MAG</span>
      </Link>
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors">
        <ArrowLeft size={16} /> Back To VLMAG
      </Link>
    </div>
  </header>
);

function SubmitForm() {
  const [form, setForm] = useState({ artist: "", title: "", artist_handle: "", email: "", external_url: "", genre: "Beat", note: "" });
  const [state, setState] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setState("");
    if (!form.artist || !form.title || !form.email || !form.external_url) { setState("error:Fill artist, track, email and a link to your sound."); return; }
    setState("sending");
    try {
      await axios.post(`${API}/radio/submit`, form);
      setState("done");
    } catch { setState("error:Couldn't send. Check your link and try again."); }
  };
  const cls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";
  if (state === "done") return (
    <div className="border border-[#E30613] bg-[#E30613]/10 p-8 text-center" data-testid="radio-submit-done">
      <p className="font-display uppercase text-2xl text-white">Sound received.</p>
      <p className="text-neutral-400 text-sm mt-3">Our team reviews every submission. If it moves the culture, you'll hear from us.</p>
    </div>
  );
  return (
    <div className="border border-white/10 p-6 md:p-8" data-testid="radio-submit-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input data-testid="radio-submit-artist" value={form.artist} onChange={set("artist")} placeholder="Artist / Producer name" className={cls} />
        <Input data-testid="radio-submit-title" value={form.title} onChange={set("title")} placeholder="Track title" className={cls} />
        <Input data-testid="radio-submit-ig" value={form.artist_handle} onChange={set("artist_handle")} placeholder="Instagram @handle" className={cls} />
        <Input data-testid="radio-submit-email" value={form.email} onChange={set("email")} placeholder="Email" className={cls} />
      </div>
      <Input data-testid="radio-submit-link" value={form.external_url} onChange={set("external_url")} placeholder="Link to your sound (SoundCloud, Drive, YouTube...)" className={`${cls} mt-3`} />
      <textarea data-testid="radio-submit-note" value={form.note} onChange={set("note")} rows={3} placeholder="Tell us about it (optional)" className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-3 text-sm mt-3" />
      {state.startsWith("error") && <p className="text-[#E30613] text-sm mt-3" data-testid="radio-submit-error">{state.slice(6)}</p>}
      <button onClick={submit} disabled={state === "sending"} data-testid="radio-submit-btn" className="mt-4 inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
        {state === "sending" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Submit Your Sound
      </button>
    </div>
  );
}

export default function RadioPage() {
  const { tracks, current, playing, playTrack, toggle, pause } = useRadio();
  const [genre, setGenre] = useState("ALL");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = useMemo(() => genre === "ALL" ? tracks : tracks.filter((t) => (t.genre || "") === genre), [tracks, genre]);
  const spotlight = useMemo(() => tracks.find((t) => t.featured), [tracks]);

  const onPlay = (t) => {
    if (t.audio_url) {
      setExpanded(null);
      if (current && current.id === t.id) toggle();
      else playTrack(t, tracks);
      return;
    }
    const emb = embedFor(t.external_url);
    if (emb) { pause(); setExpanded((id) => (id === t.id ? null : t.id)); }
  };

  return (
    <div className="bg-black text-white min-h-screen" data-testid="radio-page">
      <TopBar />

      {/* Hero */}
      <section className="px-5 md:px-10 pt-[108px] md:pt-[132px] pb-10">
        <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-[#E30613] mb-4">
          <RadioIcon size={14} /> On Air
        </p>
        <h1 className="font-display uppercase leading-[0.85] tracking-tight text-5xl sm:text-6xl md:text-8xl">VLMAG<br />Radio</h1>
        <p className="text-lg text-neutral-300 mt-6 max-w-2xl">The Sound of the Culture — exclusive drops, unreleased beats and the next wave of talent from across the network.</p>
        <div className="mt-8">
          <motion.button
            onClick={() => toggle()}
            data-testid="radio-play-station"
            animate={!playing && !current ? { boxShadow: ["0 0 0 0 rgba(227,6,19,0.55)", "0 0 0 16px rgba(227,6,19,0)"] } : { boxShadow: "0 0 0 0 rgba(227,6,19,0)" }}
            transition={!playing && !current ? { duration: 1.8, repeat: Infinity, ease: "easeOut" } : { duration: 0.3 }}
            className="inline-flex items-center gap-3 px-7 py-4 text-xs uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors"
          >
            {playing ? <Pause size={16} fill="#fff" /> : <Play size={16} fill="#fff" />} {playing ? "Pause" : "Play The Station"}
          </motion.button>
          {!playing && !current && (
            <p className="mt-3 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 flex items-center gap-2" data-testid="radio-play-hint">
              <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E30613] opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#E30613]" /></span>
              Tap in
            </p>
          )}
        </div>
      </section>

      {/* Artist spotlight */}
      {spotlight && (
        <section className="px-5 md:px-10 py-10 border-t border-white/10" data-testid="radio-spotlight">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-6">Artist Spotlight</p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="relative aspect-square overflow-hidden border border-white/10 max-w-md">
                {spotlight.artwork && <img src={mediaUrl(spotlight.artwork)} alt={spotlight.artist} className="w-full h-full object-cover grayscale-img" />}
              </div>
            </div>
            <div className="lg:col-span-7">
              <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9]">{spotlight.artist}</h2>
              <p className="text-neutral-400 mt-4 text-lg">{spotlight.title}{spotlight.genre ? ` · ${spotlight.genre}` : ""}</p>
              {embedFor(spotlight.external_url) && (
                <div className="mt-6 max-w-xl">
                  <iframe
                    title={`${spotlight.artist} player`}
                    data-testid="radio-spotlight-embed"
                    src={embedFor(spotlight.external_url).src}
                    width="100%" height={embedFor(spotlight.external_url).height} frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-6">
                {spotlight.audio_url && (
                  <button onClick={() => onPlay(spotlight)} className="inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors">
                    {current && current.id === spotlight.id && playing ? <Pause size={14} /> : <Play size={14} />} Play
                  </button>
                )}
                {spotlight.external_url && embedFor(spotlight.external_url) && (
                  <a href={spotlight.external_url} target="_blank" rel="noopener noreferrer" data-testid="radio-spotlight-listen" className="inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
                    <ExternalLink size={14} /> Open On {embedFor(spotlight.external_url).platform}
                  </a>
                )}
                {spotlight.artist_handle && (
                  <a href={`https://instagram.com/${spotlight.artist_handle.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors">
                    {spotlight.artist_handle.startsWith("@") ? spotlight.artist_handle : `@${spotlight.artist_handle}`}
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Station queue */}
      <section className="px-5 md:px-10 py-12 border-t border-white/10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none">The Station</h2>
          <div className="flex flex-wrap gap-2" data-testid="radio-genres">
            {GENRES.map((g) => (
              <button key={g} onClick={() => setGenre(g)} data-testid={`radio-genre-${g.toLowerCase()}`} className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${genre === g ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/20 text-neutral-400 hover:text-white hover:border-white/60"}`}>{g}</button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-neutral-600 py-16 text-center border border-white/10" data-testid="radio-empty">No tracks in this lane yet. Check back soon.</p>
        ) : (
          <div className="border-y border-white/10" data-testid="radio-track-list">
            {filtered.map((t, i) => {
              const active = current && current.id === t.id;
              const emb = embedFor(t.external_url);
              const playable = t.audio_url || emb;
              const isOpen = expanded === t.id;
              return (
                <div key={t.id} data-testid={`radio-track-${t.id}`} className="border-b border-white/5 last:border-b-0">
                  <div className={`group flex items-center gap-4 py-3.5 px-2 transition-colors ${active || isOpen ? "bg-white/[0.04]" : "hover:bg-white/[0.03]"}`}>
                    <button onClick={() => onPlay(t)} disabled={!playable} data-testid={`radio-play-${t.id}`} className="shrink-0 w-11 h-11 relative overflow-hidden border border-white/10 flex items-center justify-center disabled:opacity-40">
                      {t.artwork ? <img src={mediaUrl(t.artwork)} alt="" className="absolute inset-0 w-full h-full object-cover grayscale-img" /> : null}
                      <span className={`relative z-10 bg-black/50 w-full h-full flex items-center justify-center text-white transition-opacity ${(active && playing) || isOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                        {(active && playing) || isOpen ? <Pause size={16} fill="#fff" /> : <Play size={16} fill="#fff" />}
                      </span>
                    </button>
                    <span className={`font-display text-lg w-6 shrink-0 text-center ${active || isOpen ? "text-[#E30613]" : "text-neutral-600"}`}>{i + 1}</span>
                    <button onClick={() => onPlay(t)} disabled={!playable} className="min-w-0 flex-1 text-left disabled:cursor-default">
                      <p className={`text-sm font-bold truncate ${active || isOpen ? "text-[#E30613]" : "text-white"}`}>{t.title}</p>
                      <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 truncate">{t.artist}{emb && !t.audio_url ? ` · ${emb.platform}` : ""}</p>
                    </button>
                    {t.genre && <span className="hidden sm:inline text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-600 shrink-0">{t.genre}</span>}
                    {t.external_url && (
                      <a href={t.external_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="shrink-0 text-neutral-600 hover:text-white transition-colors" title="Open on platform"><ExternalLink size={15} /></a>
                    )}
                  </div>
                  {isOpen && emb && (
                    <div className="px-2 pb-4 pt-1" data-testid={`radio-embed-${t.id}`}>
                      <iframe
                        title={`${t.artist} — ${t.title}`}
                        src={emb.src}
                        width="100%" height={emb.height} frameBorder="0"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        style={{ borderRadius: 0 }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Submit */}
      <section className="px-5 md:px-10 py-16 md:py-24 border-t border-white/10">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">For The Creatives</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9] mb-4">Submit Your Sound</h2>
          <p className="text-neutral-400 mb-8 max-w-xl">Producer, singer, rapper, engineer — if it's got the vibe, we want to hear it. Drop your track and our team will review it for the station.</p>
          <SubmitForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
